import PropTypes from 'prop-types';

import Container, { ContainerProps as MuiContainerProps } from '@mui/material/Container';
import { Box, Link, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ClassityTextIcon,
  Email,
  HomeIcon,
  WhatsAppIcon,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { windowExists } from '@classity-edtech/ui-base';
import { useConfig } from '../../../contexts';
import { webPaths } from '../../../routes/paths';
import { resetFlashMessage, updateFlashMessage } from '../../../store/actions/flashMessage';
import { useAppDispatch } from '../../../store/hooks';

export interface FooterProps extends MuiContainerProps {
  legals: {
    terms: string;
    privacy: string;
  };
  contactInfo: {
    email: string;
    phone: string;
    address: string;
    addressLink: string;
  };
  companyInfo: {
    copyRight: string;
    companyNumber: string;
    quotation: string;
  };
  titles: {
    information: string;
    about: string;
  };
  aboutUs: {
    about: string;
    join: string;
    school: string;
    spaces: string;
  };
  educators: {
    signup: string;
    signin: string;
  };
  socials: {
    title: string;
    url: string;
    icon: string;
  }[];
}

export const Footer = ({ className, ...props }: FooterProps) => {
  const dispatch: any = useAppDispatch();
  const { learnUi } = useConfig() as any;
  const learnUiUrl = learnUi?.url;

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);

      dispatch(
        updateFlashMessage({
          open: true,
          severity: 'success',
          message: 'Text copied',
        }),
      );

      setTimeout(() => dispatch(resetFlashMessage()), 7000);
    } catch (err) {
      dispatch(
        updateFlashMessage({
          open: true,
          severity: 'error',
          message: 'Failed to copy text',
        }),
      );

      setTimeout(() => dispatch(resetFlashMessage()), 7000);
    }
  };

  return (
    <Box
      sx={{
        background: globalColours['$global-colour-secondary-brand-4'],
        color: globalColours['$global-colour-base-contrast'],
        px: { xs: 0.5, md: 2 },
        py: { xs: 3, md: 6 },
      }}
    >
      <Container>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 0.8fr' },
            gap: { xs: 0, md: 4 },
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Stack>
            <Typography variant='body3SemiBold' sx={{ mb: 0.5 }}>
              {props?.titles?.information}
            </Typography>
            <Link
              sx={{
                textDecoration: 'none',
                cursor: 'pointer',
                width: 'fit-content',
              }}
              href={webPaths.teach}
            >
              <Typography
                variant='body3'
                sx={{
                  color: globalColours['$global-colour-base-contrast'],
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {props?.aboutUs?.join}
              </Typography>
            </Link>
            <Link
              sx={{
                textDecoration: 'none',
                cursor: 'pointer',
                width: 'fit-content',
              }}
              href={`${learnUiUrl}/teach/signup`}
              target='_blank'
            >
              <Typography
                variant='body3'
                sx={{
                  color: globalColours['$global-colour-base-contrast'],
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {props?.educators?.signup}
              </Typography>
            </Link>
            <Link
              sx={{
                textDecoration: 'none',
                cursor: 'pointer',
                width: 'fit-content',
              }}
              href={`${learnUiUrl}/teach/signin`}
              target='_blank'
            >
              <Typography
                variant='body3'
                sx={{
                  color: globalColours['$global-colour-base-contrast'],
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {props?.educators?.signin}
              </Typography>
            </Link>
          </Stack>
          <Stack
            sx={{
              px: { xs: 0, md: 1 },
            }}
          >
            <Typography variant='body3SemiBold' sx={{ mb: 0.5 }}>
              {props?.titles?.about}
            </Typography>
            <Link
              sx={{
                textDecoration: 'none',
                cursor: 'pointer',
                width: 'fit-content',
              }}
              href={webPaths.about}
            >
              <Typography
                variant='body3'
                sx={{
                  color: globalColours['$global-colour-base-contrast'],
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {props?.aboutUs?.about}
              </Typography>
            </Link>
            <Link
              sx={{
                textDecoration: 'none',
                cursor: 'pointer',
                width: 'fit-content',
              }}
              href={'/'}
            >
              <Typography
                variant='body3'
                sx={{
                  color: globalColours['$global-colour-base-contrast'],
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {props?.aboutUs?.school}
              </Typography>
            </Link>
            <Link
              sx={{
                textDecoration: 'none',
                cursor: 'pointer',
                width: 'fit-content',
              }}
              href={`/${webPaths.spaces}`}
            >
              <Typography
                variant='body3'
                sx={{
                  color: globalColours['$global-colour-base-contrast'],
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {props?.aboutUs?.spaces}
              </Typography>
            </Link>
          </Stack>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 1,
              width: '100%',
              pl: 2,
              justifyContent: 'space-between',
            }}
          >
            {props?.socials?.map((social, index) => (
              <Link
                key={index}
                href={social?.url}
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  alignItems: 'center',
                  height: 'fit-content',
                  cursor: 'pointer',
                  color: globalColours['$global-colour-base-contrast'],
                  textDecoration: 'none',
                  '&:hover': {
                    color: globalColours['$global-colour-palette-gray-100'],
                  },
                }}
                target='_blank'
              >
                <img src={social?.icon} style={{ width: '20px', height: '20px' }} />
                <Typography variant='body3'>{social?.title}</Typography>
              </Link>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            display: { xs: 'flex', md: 'grid' },
            gridTemplateColumns: '1fr 1fr 0.8fr',
            flexWrap: 'wrap-reverse',
            alignItems: 'center',
            gap: { xs: 0, md: 4 },
            justifyContent: 'flex-end',
          }}
        >
          <Stack
            sx={{
              gap: { xs: 0, md: 0.5 },
              flexDirection: { xs: 'row', md: 'column' },
              justifyContent: 'space-between',
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 0.5, md: 1 },
                alignItems: 'center',
                flex: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  color: globalColours['$global-colour-palette-gray-100'],
                },
              }}
              onClick={() => copyText(props?.contactInfo?.email)}
            >
              <Email sx={{ width: '16px', height: '16px' }} />
              <Typography variant='body3'>{props?.contactInfo?.email}</Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 0.5, md: 1 },
                alignItems: 'center',
                flex: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  color: globalColours['$global-colour-palette-gray-100'],
                },
              }}
              onClick={() => copyText(props?.contactInfo?.phone)}
            >
              <WhatsAppIcon sx={{ width: '16px', height: '16px' }} />
              <Typography variant='body3'>{props?.contactInfo?.phone}</Typography>
            </Box>
          </Stack>
          <Stack
            sx={{
              gap: { xs: 0, md: 0.5 },
              display: { xs: 'none', md: 'flex' },
              px: 1,
            }}
          >
            <ClassityTextIcon sx={{ width: '320px', height: 'auto' }} />
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
                cursor: 'pointer',
                '&:hover': {
                  color: globalColours['$global-colour-palette-gray-100'],
                },
              }}
              onClick={() => copyText(props?.contactInfo?.addressLink)}
            >
              <HomeIcon sx={{ width: '16px', height: '16px' }} />
              <Typography variant='body3'>{props?.contactInfo?.address}</Typography>
            </Box>
          </Stack>
          <Box
            sx={{
              display: { xs: 'flex', md: 'block' },
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: { xs: 2, md: 0 },
            }}
          >
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                gap: 3,
                flex: 0.5,
                width: '100%',
              }}
            >
              {props?.socials?.map((social, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                    height: 'fit-content',
                    cursor: 'pointer',
                    '&:hover': {
                      color: globalColours['$global-colour-palette-gray-100'],
                    },
                  }}
                >
                  <img src={social?.icon} style={{ width: '20px', height: '20px' }} />
                </Box>
              ))}
            </Box>
            <Stack
              sx={{
                gap: { xs: 0, md: 0.5 },
                flex: 0.5,
                pl: { xs: 0, md: 2 },
              }}
            >
              <Box sx={{ width: 'fit-content', ml: 'auto' }}>
                <Typography
                  variant='body3'
                  sx={{ textAlign: { xs: 'left', md: 'right' }, display: 'block' }}
                >
                  {props?.companyInfo?.quotation}
                </Typography>
                <Typography
                  variant='body3'
                  sx={{ textAlign: { xs: 'left', md: 'right' }, display: 'block' }}
                >
                  {props?.companyInfo?.companyNumber}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Box>
        <ClassityTextIcon
          sx={{
            width: '100%',
            height: 'auto',
            mb: -3,
            mt: 3,
            display: { xs: 'block', md: 'none' },
          }}
        />

        {/* Tail-end */}
        <Box
          sx={{
            borderTop: {
              xs: 'none',
              md: `1px solid ${globalColours['$global-colour-base-contrast']}`,
            },
            pt: { xs: 1, md: 2 },
            mt: { xs: 4, md: 2 },
            display: { xs: 'flex', md: 'grid' },
            gridTemplateColumns: { xs: 'none', md: '1fr 1fr 0.8fr' },
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 0, md: 2 },
            alignItems: { xs: 'flex-start', md: 'center' },
          }}
        >
          {/* Mobile: terms & privacy on top */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              gap: 2,
              pb: 0.5,
            }}
          >
            <Link
              href={`/${webPaths.termsOfUse}`}
              sx={{ textDecoration: 'none', cursor: 'pointer' }}
            >
              <Typography
                variant='body3'
                sx={{
                  color: globalColours['$global-colour-base-contrast'],
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {props?.legals?.terms}
              </Typography>
            </Link>
            <Link
              href={`/${webPaths.privacyPolicy}`}
              sx={{ textDecoration: 'none', cursor: 'pointer' }}
            >
              <Typography
                variant='body3'
                sx={{
                  color: globalColours['$global-colour-base-contrast'],
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {props?.legals?.privacy}
              </Typography>
            </Link>
          </Box>

          {/* Mobile: copyright below; Desktop: copyright left (flex: 1) */}
          <Box sx={{ flex: { xs: 'unset', md: 1 } }}>
            <Typography
              variant='body3'
              sx={{ color: globalColours['$global-colour-base-contrast'] }}
            >
              {props?.companyInfo?.copyRight}
            </Typography>
          </Box>

          {/* Desktop: terms & privacy centre (flex: 1) */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flex: 1,
              gap: 2,
              justifyContent: 'left',
              px: { xs: 'unset', md: 1 },
            }}
          >
            <Link
              href={`/${webPaths.termsOfUse}`}
              sx={{ textDecoration: 'none', cursor: 'pointer' }}
            >
              <Typography
                variant='body3'
                sx={{
                  color: globalColours['$global-colour-base-contrast'],
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {props?.legals?.terms}
              </Typography>
            </Link>
            <Link
              href={`/${webPaths.privacyPolicy}`}
              sx={{ textDecoration: 'none', cursor: 'pointer' }}
            >
              <Typography
                variant='body3'
                sx={{
                  color: globalColours['$global-colour-base-contrast'],
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {props?.legals?.privacy}
              </Typography>
            </Link>
          </Box>

          {/* Desktop: empty right column (flex: 1) */}
          <Box sx={{ display: { xs: 'none', md: 'block' }, flex: 1 }} />
        </Box>
      </Container>
    </Box>
  );
};

Footer.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Footer.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Footer)(
  ({ theme }) => `
  }
`,
);
